import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Callout, Chip, HoverCard, Segmented } from '@/components/ui';
import type { RoleMapTableVM } from './useRoleMapTable';
import styles from './RoleMapScopePanel.module.css';
import shared from './roleMapShared.module.css';

/**
 * EAA-1817 (frame 59): "Đang sửa cho" — scope context above the list table.
 * The toolbar's Phạm vi filter still switches scope; this panel explains what
 * that slice means (wholesale replacement, not inheritance) and, in org scope,
 * surfaces the +/- diff against the default using data already on screen.
 *
 * EAA-1817 (2026-08-24): COMPACTED to one row. The panel used to print its
 * explanation as a paragraph, and the list view stacked two more boxes under it
 * (the bulk-disabled note and the degraded banner) — about 250px of prose before
 * the first table row, on a screen whose whole job is the table. The explanation
 * is now behind the `(i)`, which is this console's own convention for exactly
 * this (design frame 98: "Mô tả không in ra dải tiêu đề"; `PageHeader` has done
 * it since EAA-1846), and the bulk-disabled reason moved INTO this panel as a
 * one-line hint rather than a banner of its own — it is a fact about the
 * selected scope, which is what this panel is for.
 *
 * Nothing was dropped: every sentence that used to be printed is still on the
 * screen, either in the `(i)` or as the same hint text. The only alert still
 * rendered as a box is the degraded banner, because it is an alert.
 */
export function RoleMapScopePanel({ vm }: { vm: RoleMapTableVM }) {
    const { t } = useTranslation();

    const orgName = vm.actingOrgName ?? t('console.roleMap.scopePanel.orgFallback');

    const scopeOptions = useMemo(() => {
        const options = [];
        if (vm.canManageDefault) {
            options.push({
                label: t('console.roleMap.scopePanel.scopeDefault'),
                value: 'default',
            });
        }
        options.push({
            label: orgName,
            value: 'org',
        });
        return options;
    }, [vm.canManageDefault, orgName, t]);

    const showOrgCallout = vm.scope === 'org' && Boolean(vm.actingOrgId);
    const hasDiffs = vm.scopeOverrideSummaries.length > 0;

    return (
        <section
            className={styles.scopePanel}
            aria-label={t('console.roleMap.scopePanel.sectionAriaLabel')}
        >
            <div className={styles.scopePanelHead}>
                <h3 className={styles.scopePanelTitle}>{t('console.roleMap.scopePanel.title')}</h3>
                <HoverCard
                    trigger={
                        <span
                            className={styles.scopePanelInfo}
                            role="img"
                            aria-label={t('console.roleMap.scopePanel.introAriaLabel')}
                        >
                            i
                        </span>
                    }
                    content={
                        <Trans
                            i18nKey={
                                vm.scope === 'default'
                                    ? 'console.roleMap.scopePanel.introDefault'
                                    : 'console.roleMap.scopePanel.introOrg'
                            }
                            values={{ orgName }}
                            components={{ strong: <strong /> }}
                        />
                    }
                />
                <Segmented
                    options={scopeOptions}
                    value={vm.scope}
                    onChange={(next) => vm.setScope(next as 'default' | 'org')}
                    aria-label={t('console.roleMap.scopePanel.scopeToggleAriaLabel')}
                />
                {/* EAA-1837's honest degrade, moved here from its own banner:
                    row selection and every bulk action are unavailable in the
                    Mặc định slice because the endpoints are org-scoped and skip
                    `organization_id IS NULL` rows. It belongs beside the scope
                    control it is about. */}
                {vm.bulkDisabledReason && (
                    <p className={styles.scopePanelHint}>{vm.bulkDisabledReason}</p>
                )}
            </div>

            {showOrgCallout && (
                <Callout tone="warning" label={t('console.roleMap.scopePanel.orgCalloutLabel')}>
                    <p className={styles.scopePanelCalloutText}>
                        <Trans
                            i18nKey="console.roleMap.scopePanel.orgCalloutBody"
                            values={{ orgName }}
                            components={{ strong: <strong /> }}
                        />
                    </p>
                </Callout>
            )}

            {showOrgCallout && hasDiffs && (
                <div className={styles.scopePanelDiff}>
                    <h4 className={styles.scopePanelDiffTitle}>
                        {t('console.roleMap.scopePanel.diffTitle')}
                    </h4>
                    <ul className={styles.scopePanelDiffList}>
                        {vm.scopeOverrideSummaries.map((entry) => (
                            <li
                                key={`${entry.application}::${entry.role_key}`}
                                className={styles.scopePanelDiffRow}
                            >
                                <code className={shared.roleKey}>{entry.role_key}</code>
                                <span className={styles.scopePanelDiffChips}>
                                    {entry.added.map((role) => (
                                        <Chip key={`add-${role}`} tone="mapped">
                                            +{role}
                                        </Chip>
                                    ))}
                                    {entry.removed.map((role) => (
                                        <Chip key={`rem-${role}`} tone="stale">
                                            −{role}
                                        </Chip>
                                    ))}
                                </span>
                                {vm.canManageOrg && (
                                    <button
                                        type="button"
                                        className={styles.scopePanelDiffAction}
                                        onClick={() =>
                                            vm.openScopeDiff({
                                                key: `${entry.application}::${entry.role_key}`,
                                                role_key: entry.role_key,
                                                application: entry.application,
                                                appName: entry.appName,
                                                ids: [],
                                                appRoles: [],
                                                organization_id: vm.actingOrgId,
                                                isStale: false,
                                                accountCount: 0,
                                            })
                                        }
                                    >
                                        {t('console.roleMap.scopePanel.comparePair')}
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </section>
    );
}

export default RoleMapScopePanel;
